import { ArrowLinkText } from '@/components/common/ArrowLinkText';
import { InfoButtonWithModal } from '@/components/common/InfoModal';
import { useTrackModalShown } from '@/services/analytics/useTracking';
import { toDollarsWithCents } from '@/util/currency';
import { useQuery } from '@apollo/client';
import {
    Box,
    BoxProps,
    Divider,
    FlexProps,
    ModalCloseButton,
    Skeleton,
    Stack,
    Table,
    Tbody,
    Td,
    Th,
    Thead,
    Tr,
} from '@chakra-ui/react';
import { FinancialEntityPositionsDocument } from '@common/graphql/generated';
import styleguide from '@common/styleguide';
import { FinancialEntityPosition } from '@common/types';
import { sortBy } from '@common/util/arrays';
import pluralize from '@common/util/pluralize';
import { toSentenceCase } from '@common/util/strings';
import { isNumber, isString } from '@common/util/types';
import React from 'react';
import { FlexCol } from '../common/Flex';
import Modal from '../common/modals/Modal';
import { Header, Paragraph, Text, Title } from '../common/Typography';
import ModalManager, { useModal } from '../ModalManager';

export interface GoalDetailPositionsItem extends FlexProps {
    financialEntityId: string;
}

const LoadingRows = () => (
    <>
        <Divider my={8} />
        <Stack spacing={5}>
            <Skeleton w={180} h={5} />
            <Skeleton w={320} h={5} />
            <Skeleton w={50} h={5} />
        </Stack>
    </>
);

const GoalDetailPositionsItem = ({
    financialEntityId,
    ...props
}: GoalDetailPositionsItem) => {
    const { positions, loading } =
        useFinancialEntityPositions(financialEntityId);

    if (loading || !positions?.length) {
        return <LoadingRows />;
    }

    const filteredPositions = sortBy(
        positions.filter((p) => p.symbol !== 'CASH_USD'),
        (p) => Number(p.totalValue),
        'DESC'
    ).slice(0, 6);

    return (
        <>
            <Divider my={8} />
            <Box {...props}>
                <Paragraph mb={4} lineHeight={1}>
                    <Title fontWeight="medium" as="span" mr={1}>
                        {positions?.length}
                    </Title>{' '}
                    individual{' '}
                    {pluralize('holding', positions?.length || 0, false)}
                    <InfoButtonWithModal
                        title="Individual Holdings"
                        buttonProps={{
                            position: 'relative',
                            top: '-1px',
                            onClickEventParams: {
                                location: 'Goal Detail',
                            },
                        }}
                        color="gray"
                    >
                        <Paragraph>
                            A holding could be cash, a stock, or an ETF.
                            Together, your holdings combine to become your
                            diversified portfolio. With your direct indexing
                            portfolio, we invest directly in US stocks (instead
                            of using ETFs) to maximize your tax loss harvesting.
                            For your exposure to bonds, treasury and
                            international stocks, we use ETFs because it is the
                            most efficient + cost effective way to give you
                            access.
                        </Paragraph>
                    </InfoButtonWithModal>
                </Paragraph>
                <Box color="textMuted" mb={5}>
                    {filteredPositions.map((position) => (
                        <Text
                            key={position.symbol}
                            display="inline-block"
                            fontSize="xs"
                            lineHeight={1}
                            border="1px solid"
                            borderColor="gray.300"
                            borderRadius="full"
                            mr={1}
                            py={0.5}
                            px={2}
                            cursor="default"
                        >
                            {position.symbol}
                        </Text>
                    ))}
                </Box>
                <ArrowLinkText
                    onClick={() => showPositionsModal({ positions })}
                >
                    See all
                </ArrowLinkText>
            </Box>
        </>
    );
};

export interface PositionsViewProps extends FlexProps {
    positions: FinancialEntityPosition[];
}

export const showPositionsModal = (props: PositionsViewProps) =>
    ModalManager.show(PositionsModal, props);

export const PositionsModal = ModalManager.create<PositionsViewProps>(
    (props) => {
        useTrackModalShown('Goal Positions');

        const modal = useModal();

        return (
            <Modal
                size="6xl"
                isOpen={modal.visible}
                onClose={modal.remove}
                unstyled
            >
                <PositionsView {...props} />
            </Modal>
        );
    }
);

const headerCol = <K extends keyof FinancialEntityPosition>(hc: {
    key: K;
    label: string;
    formatter?: <T extends FinancialEntityPosition[K]>(input: T) => string;
    styles?: <T extends FinancialEntityPosition[K]>(input: T) => BoxProps;
    headerStyles?: BoxProps;
}) => hc;

const HEADER_COLS: ReturnType<typeof headerCol>[] = [
    headerCol({ key: 'name', label: 'Name' }),
    headerCol({ key: 'symbol', label: 'Symbol' }),
    headerCol({
        key: 'industry',
        label: 'Industry',
        formatter: (input) => (isString(input) ? toSentenceCase(input) : ''),
        styles: () => ({
            whiteSpace: 'nowrap',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            maxW: 36,
        }),
        headerStyles: { maxW: 36 },
    }),
    headerCol({
        key: 'shares',
        label: 'Shares',
        formatter: (input) => (isNumber(input) ? String(input) : ''),
    }),
    headerCol({
        key: 'sharePrice',
        label: 'Share price',
        formatter: (input) =>
            isNumber(input) ? toDollarsWithCents(input) : '',
    }),
    headerCol({
        key: 'totalValue',
        label: 'Total value',
        formatter: (input) =>
            isNumber(input) ? toDollarsWithCents(input) : '',
    }),
    headerCol({
        key: 'initialCost',
        label: 'Initial cost',
        formatter: (input) =>
            isNumber(input) ? toDollarsWithCents(input) : '',
    }),
    headerCol({
        key: 'totalReturnRealized',
        label: 'Total return (realized)',
        formatter: (input) =>
            isNumber(input) ? toDollarsWithCents(input) : '',
        styles: (input) =>
            isNumber(input)
                ? { color: input >= 0 ? 'green.600' : 'red.500' }
                : {},
    }),
    headerCol({
        key: 'totalReturnUnrealized',
        label: 'Total return (unrealized)',
        formatter: (input) =>
            isNumber(input) ? toDollarsWithCents(input) : '',
        styles: (input) =>
            isNumber(input)
                ? { color: input >= 0 ? 'green.600' : 'red.500' }
                : {},
    }),
];

const PositionsView = ({ positions, ...props }: PositionsViewProps) => {
    return (
        <FlexCol overflow="hidden" pt={12} px={8} pb={4} {...props}>
            <ModalCloseButton />
            <Header mb={6}>
                {pluralize('holding', positions.length, true)}{' '}
                {positions.length === 1 ? 'is' : 'are'} helping you reach your
                goal
            </Header>
            <FlexCol grow={1} overflow="auto">
                <Table position="relative">
                    <Thead>
                        <Tr>
                            {HEADER_COLS.map(({ key, label, headerStyles }) => (
                                <Th
                                    top={0}
                                    position="sticky"
                                    bg="white"
                                    key={'header' + key}
                                    boxShadow={`inset 0 -1px 0 ${styleguide.semantic.textDark}`}
                                    {...(headerStyles ?? {})}
                                >
                                    {label}
                                </Th>
                            ))}
                        </Tr>
                    </Thead>
                    <Tbody>
                        {positions.map((p) => (
                            <Tr key={'row' + p.symbol}>
                                {HEADER_COLS.map(
                                    ({ key, formatter, styles }) => (
                                        <Td
                                            key={'cell' + p.symbol + key}
                                            {...(styles ? styles(p[key]) : {})}
                                        >
                                            {formatter
                                                ? formatter(p[key])
                                                : p[key]}
                                        </Td>
                                    )
                                )}
                            </Tr>
                        ))}
                    </Tbody>
                </Table>
            </FlexCol>
        </FlexCol>
    );
};

const useFinancialEntityPositions = (
    financialEntityId: string
): { positions?: FinancialEntityPosition[]; loading: boolean } => {
    const { data, loading } = useQuery(FinancialEntityPositionsDocument, {
        variables: { financialEntityId },
        fetchPolicy: 'no-cache',
    });
    if (!data) return { loading };

    return {
        positions: sortBy(
            (data.financialEntityPositions as FinancialEntityPosition[]) ?? [],
            // TODO: See if we need to handle positions without names
            (position) => position.name ?? ''
        ),
        loading,
    };
};

export default GoalDetailPositionsItem;
